<form [formGroup]="form" class="request-form">
  <div class="form-section">
    <mat-form-field
      #descriptionField
      appearance="outline"
      class="full-width"
      floatLabel="always"
    >
      <mat-label>Request Description</mat-label>
      <textarea
        matInput
        formControlName="description"
        rows="5"
        [maxlength]="descriptionMaxLength"
        (keydown)="onDescriptionKeydown($event)"
        (paste)="onDescriptionPaste($event)"
        placeholder="Please provide a detailed description of the nature of this request, including specific requirements, timeline, and any relevant background information..."
      ></textarea>
      <mat-hint
        align="end"
        [style.color]="descriptionLimitReached ? 'var(--error-color)' : null"
        [style.font-weight]="descriptionLimitReached ? '600' : null"
        [style.transition]="'color 0.15s ease'"
        >{{ descriptionLength }} / {{ descriptionMaxLength }}</mat-hint
      >
    </mat-form-field>
  </div>

  <div class="form-row two-columns">
    <div class="form-field-wrapper">
      <mat-form-field appearance="outline" floatLabel="always">
        <mat-label>Request Type</mat-label>
        <mat-select formControlName="requestType">
          @for (type of requestTypes; track type.value) {
            <mat-option [value]="type.value">{{ type.label }}</mat-option>
          }
        </mat-select>
      </mat-form-field>
    </div>
    <!-- Spacer keeps Request Type at half width. -->
    <div class="form-field-wrapper"></div>
  </div>

  <!-- Requestors: label, then search, then selected chips -->
  <div class="form-row section-row">
    <div class="form-field-wrapper">
      <label class="field-label">Requestors</label>

      <div class="requestor-field-wrapper half-width-search">
        <div class="requestor-input-container">
          <mat-form-field appearance="outline" class="requestor-search-field">
            <mat-icon matPrefix>search</mat-icon>
            <input
              matInput
              formControlName="requestorSearch"
              placeholder="Search requestors to add..."
              (input)="onRequestorInput($event)"
              (focus)="onRequestorFocus()"
              (blur)="onRequestorBlur()"
              (keyup.enter)="onSelectRequestorBySearch()"
            />
            @if (isLoadingRequestor) {
              <mat-spinner
                matSuffix
                diameter="16"
                class="field-spinner"
              ></mat-spinner>
            }
          </mat-form-field>
          @if (isLoadingDefaultRequestor) {
            <div class="requestor-skeleton-loader">
              <span class="skeleton-pulse"></span>
            </div>
          }
        </div>

        @if (requestorSuggestions.length > 0) {
          <div class="requestor-suggestions">
            <div class="requestor-suggestions-scroll">
              @for (r of requestorSuggestions; track r.employeeId) {
                <div class="suggestion-item" (click)="selectRequestor(r)">
                  <div class="requestor-info">
                    <span class="requestor-name">{{ r.fullName }}</span>
                    <span class="requestor-role">{{
                      r.title || "Unknown"
                    }}</span>
                  </div>
                </div>
              }
            </div>
          </div>
        } @else if (requestorNotFound && requestorSearchTerm.trim().length >= 3) {
          <div class="requestor-suggestions">
            <div class="suggestion-item no-results">
              <div class="requestor-info">
                <span class="no-results-text"
                  >No search results for {{ requestorSearchTerm }}</span
                >
              </div>
            </div>
          </div>
        }
      </div>

      <div class="selected-chips-panel">
        @if (selectedRequestors.length > 0) {
          @for (requestor of selectedRequestors; track requestor.id) {
            <div class="selected-chip">
              <span class="chip-text">
                <span class="chip-label"
                  >{{ requestor.name
                  }}{{ requestor.isCurrentUser ? " (You)" : "" }}</span
                >
                <span class="chip-sub">{{ requestor.role }}</span>
              </span>
              <button
                mat-icon-button
                type="button"
                class="remove-button"
                (click)="removeRequestor(requestor.id)"
                title="Remove requestor"
              >
                <mat-icon class="remove-icon">remove_circle</mat-icon>
              </button>
            </div>
          }
        } @else {
          <span class="selected-chips-placeholder">
            No requestors added yet
          </span>
        }
      </div>
    </div>
  </div>

  <!-- Practice Areas: label (+count), then search, then selected chips -->
  <div class="form-row section-row">
    <div class="form-field-wrapper">
      <label class="field-label">
        Practice Areas
        @if (selectedPracticeAreaCount > 0) {
          <span class="resource-count">({{ selectedPracticeAreaCount }})</span>
        }
      </label>

      <div
        class="practice-area-field-wrapper half-width-search"
        [class.has-error]="
          !!form.get('practiceAreaCodes')?.hasError('requestorCoverage')
        "
      >
        <mat-form-field appearance="outline" class="practice-area-search-field">
          <mat-icon matPrefix>search</mat-icon>
          <input
            matInput
            [value]="practiceAreaSearchTerm"
            placeholder="Search practice areas to add..."
            autocomplete="off"
            (input)="onPracticeAreaInput($event)"
            (focus)="onPracticeAreaFocus()"
            (blur)="onPracticeAreaBlur()"
          />
        </mat-form-field>

        @if (practiceAreaDropdownOpen) {
          @if (practiceAreaOptions.length > 0) {
            <div
              class="practice-area-suggestions"
              (mousedown)="$event.preventDefault()"
            >
              <div class="practice-area-suggestions-scroll">
                @for (option of practiceAreaOptions; track option.value) {
                  <div
                    class="suggestion-item"
                    [class.option-needs-coverage]="option.uncovered"
                    [class.option-selected]="option.selected"
                    (click)="togglePracticeArea(option.value)"
                  >
                    <span class="suggestion-label">{{ option.label }}</span>
                    @if (option.selected) {
                      <mat-icon class="suggestion-check">check</mat-icon>
                    }
                  </div>
                }
              </div>
            </div>
          } @else {
            <div class="practice-area-suggestions">
              <div class="suggestion-item no-results">
                <span class="no-results-text">No practice areas found</span>
              </div>
            </div>
          }
        }
      </div>

      <div
        class="selected-chips-panel"
        [class.field-error-state]="
          !!form.get('practiceAreaCodes')?.hasError('requestorCoverage')
        "
      >
        @if (selectedPracticeAreaOptions.length > 0) {
          @for (pa of selectedPracticeAreaOptions; track pa.value) {
            <div class="selected-chip">
              <span class="chip-label">{{ pa.label }}</span>
              <button
                mat-icon-button
                type="button"
                class="remove-button"
                (click)="removePracticeArea(pa.value)"
                title="Remove practice area"
              >
                <mat-icon class="remove-icon">remove_circle</mat-icon>
              </button>
            </div>
          }
        } @else {
          <span class="selected-chips-placeholder">
            No practice areas selected
          </span>
        }
      </div>

      <div class="field-error-slot">
        @if (form.get("practiceAreaCodes")?.hasError("requestorCoverage")) {
          <div class="field-error requestor-coverage-error">
            At least one of each requestor's practice areas is required for every requestor. Missing
            coverage for: {{ uncoveredRequestorNames }}.
          </div>
        }
      </div>
    </div>
  </div>
</form>
